import { Sprite } from "./Sprite";
import { Config } from "./../../Config";
import { SpriteConst, SubPassFlag, TransformKind } from "./SpriteConst";
import { NodeFlags } from "../Const";
import { Event } from "../events/Event";
import { InputManager } from "../events/InputManager";
import { Matrix } from "../maths/Matrix";
import { Point } from "../maths/Point";
import { RenderInfo } from "../renders/RenderInfo";
import { Browser } from "../utils/Browser";
import { ColorUtils } from "../utils/ColorUtils";
import { Stat } from "../utils/Stat";
import { ILaya } from "../../ILaya";
import { ComponentDriver } from "../components/ComponentDriver";
import { LayaGL } from "../layagl/LayaGL";
import { RenderState2D } from "../webgl/utils/RenderState2D";
import { Timer } from "../utils/Timer";
import { Tweener } from "../tween/Tweener";
import { Render2DProcessor } from "./Render2DProcessor";
import { Color } from "../maths/Color";
import { PAL } from "../platform/PlatformAdapters";
import { TextRenderConfig } from "../webgl/text/TextRenderConfig";
import { StatElement } from "../layagl/StatisticsContext";
export class Stage extends Sprite {
    constructor() {
        super();
        this.offset = new Point();
        this.designWidth = 0;
        this.designHeight = 0;
        this.canvasRotation = false;
        this.canvasDegree = 0;
        this.renderingEnabled = true;
        this.screenAdaptationEnabled = true;
        this._canvasTransform = new Matrix();
        this._scene3Ds = [];
        this._scene2Ds = [];
        this._frameRate = "fast";
        this._screenMode = "none";
        this._scaleMode = "noscale";
        this._alignV = "top";
        this._alignH = "left";
        this._bgColor = "gray";
        this._renderCount = 0;
        this._frameStartTime = 0;
        this._wgColor = new Color(0, 0, 0, 0);
        this._needUpdateCanvasSize = false;
        this._graphicUpdateList = new Set();
        this._subpassUpdateList = new Set();
        this._tranMatrixUpdateList = new Set();
        this.mouseEnabled = true;
        this.hitTestPrior = true;
        this._setBit(NodeFlags.DISPLAYED_INSTAGE, true);
        this._setBit(NodeFlags.ACTIVE_INHIERARCHY, true);
        this._isFocused = true;
        this._transform = new Matrix();
        this._componentDriver = new ComponentDriver();
        this.passManager = new Render2DProcessor();
        PAL.browser.on(Event.FOCUS, () => {
            if (!this._isFocused) {
                this._isFocused = true;
                this.event(Event.FOCUS);
                this.event(Event.FOCUS_CHANGE);
            }
        });
        PAL.browser.on(Event.BLUR, () => {
            if (this._isFocused) {
                this._isFocused = false;
                this.event(Event.BLUR);
                this.event(Event.FOCUS_CHANGE);
            }
        });
        PAL.browser.on(Event.VISIBILITY_CHANGE, (visible) => {
            this.renderingEnabled = visible;
            this.event(Event.VISIBILITY_CHANGE, visible);
        });
        PAL.browser.on(Event.RESIZE, () => {
            if (PAL.textInput.target)
                return;
            if (this.screenAdaptationEnabled) {
                this.event(Event.WILL_RESIZE);
                this.updateCanvasSize(true);
            }
        });
        PAL.browser.on(Event.ORIENTATION_CHANGE, (e) => {
            if (this.screenAdaptationEnabled) {
                this.event(Event.WILL_RESIZE);
                this.updateCanvasSize(true);
            }
        });
        this.passManager.basePass.root = this._struct;
        this._struct.pass = this.passManager.basePass;
    }
    _transChanged(kind) {
        super._transChanged(kind);
        if ((kind & TransformKind.Size) != 0) {
            this.designWidth = this._width;
            this.designHeight = this._height;
            this.updateCanvasSize(true);
        }
    }
    measureWidth() {
        this.needUpdateCanvasSize();
        return this._width;
    }
    measureHeight() {
        this.needUpdateCanvasSize();
        return this._height;
    }
    get isFocused() {
        return this._isFocused;
    }
    get isVisibility() {
        return PAL.browser.getVisibility();
    }
    updateCanvasSize(delay) {
        if (delay) {
            if (!this._needUpdateCanvasSize) {
                this._needUpdateCanvasSize = true;
                ILaya.systemTimer.callLater(this, this.updateCanvasSize);
            }
        }
        else {
            this.setScreenSize(Browser.clientWidth * Browser.pixelRatio, Browser.clientHeight * Browser.pixelRatio);
        }
    }
    needUpdateCanvasSize() {
        if (this._needUpdateCanvasSize)
            this.updateCanvasSize();
    }
    setScreenSize(screenWidth, screenHeight) {
        var _a;
        this._needUpdateCanvasSize = false;
        let pixelRatio = Browser.pixelRatio;
        screenWidth /= pixelRatio;
        screenHeight /= pixelRatio;
        if (this._screenMode !== Stage.SCREEN_NONE) {
            let screenType = screenWidth / screenHeight < 1 ? Stage.SCREEN_VERTICAL : Stage.SCREEN_HORIZONTAL;
            this.canvasRotation = screenType !== this._screenMode;
            if (this.canvasRotation) {
                let temp = screenHeight;
                screenHeight = screenWidth;
                screenWidth = temp;
            }
        }
        else
            this.canvasRotation = false;
        let canvas = Browser.mainCanvas;
        let mat = this._canvasTransform.identity();
        let scaleMode = this._scaleMode;
        let canvasWidth = this.designWidth;
        let canvasHeight = this.designHeight;
        let canvasScale = 1;
        if (!Browser.isDomSupported
            && (scaleMode === Stage.SCALE_NOSCALE || scaleMode == Stage.SCALE_SHOWALL || scaleMode === Stage.SCALE_NOBORDER)) {
            scaleMode = Stage.SCALE_FIXED_AUTO;
        }
        switch (scaleMode) {
            case Stage.SCALE_NOSCALE:
                break;
            case Stage.SCALE_FULL:
                canvasWidth = screenWidth;
                canvasHeight = screenHeight;
                break;
            case Stage.SCALE_SHOWALL:
                canvasScale = Math.min(screenWidth / canvasWidth, screenHeight / canvasHeight);
                break;
            case Stage.SCALE_NOBORDER:
                canvasScale = Math.max(screenWidth / canvasWidth, screenHeight / canvasHeight);
                break;
            case Stage.SCALE_FIXED_WIDTH:
            case Stage.SCALE_FIXED_HEIGHT:
            case Stage.SCALE_FIXED_AUTO:
                if (scaleMode === Stage.SCALE_FIXED_WIDTH
                    || scaleMode === Stage.SCALE_FIXED_AUTO && (screenWidth / screenHeight) < (canvasWidth / canvasHeight)) {
                    canvasScale = screenWidth / canvasWidth;
                    canvasHeight = screenHeight / canvasScale;
                }
                else {
                    canvasScale = screenHeight / canvasHeight;
                    canvasWidth = screenWidth / canvasScale;
                }
                break;
        }
        this._width = canvasWidth;
        this._height = canvasHeight;
        if (Config.useRetinalCanvas || !Browser.isDomSupported) {
            canvasWidth *= canvasScale;
            canvasHeight *= canvasScale;
            canvasScale = 1;
            if (pixelRatio > 4 && Browser.isDomSupported)
                pixelRatio = 4;
            canvasWidth *= pixelRatio;
            canvasHeight *= pixelRatio;
            canvasScale /= pixelRatio;
        }
        mat.scale(canvasScale, canvasScale);
        if (Browser.isDomSupported) {
            let offsetX = 0;
            let offsetY = 0;
            if (this._alignH === Stage.ALIGN_LEFT)
                offsetX = 0;
            else if (this._alignH === Stage.ALIGN_RIGHT)
                offsetX = screenWidth - canvasWidth * canvasScale;
            else
                offsetX = (screenWidth - canvasWidth * canvasScale) * 0.5;
            if (this._alignV === Stage.ALIGN_TOP)
                offsetY = 0;
            else if (this._alignV === Stage.ALIGN_BOTTOM)
                offsetY = screenHeight - canvasHeight * canvasScale;
            else
                offsetY = (screenHeight - canvasHeight * canvasScale) * 0.5;
            offsetX += this.offset.x;
            offsetY += this.offset.y;
            mat.translate(Math.round(offsetX), Math.round(offsetY));
        }
        if (this.canvasRotation) {
            if (this._screenMode === Stage.SCREEN_HORIZONTAL) {
                mat.rotate(Math.PI / 2);
                mat.translate(screenHeight, 0);
                this.canvasDegree = 90;
            }
            else {
                mat.rotate(-Math.PI / 2);
                mat.translate(0, screenWidth);
                this.canvasDegree = -90;
            }
        }
        else
            this.canvasDegree = 0;
        mat.a = formatData(mat.a);
        mat.d = formatData(mat.d);
        mat.tx = formatData(mat.tx);
        mat.ty = formatData(mat.ty);
        canvasWidth = Math.round(canvasWidth);
        canvasHeight = Math.round(canvasHeight);
        canvas.size(canvasWidth, canvasHeight);
        if (Browser.isDomSupported) {
            let canvasStyle = Browser.mainCanvas.source.style;
            PAL.browser.setStyleTransformOrigin(canvasStyle, "0px 0px 0px");
            PAL.browser.setStyleTransform(canvasStyle, "matrix(" + mat.toString() + ")");
            canvasStyle.width = canvasWidth + "px";
            canvasStyle.height = canvasHeight + "px";
            mat.translate(parseInt(canvasStyle.left) || 0, parseInt(canvasStyle.top) || 0);
        }
        this.scale(formatData(canvasWidth / this._width), formatData(canvasHeight / this._height));
        RenderState2D.width = canvasWidth;
        RenderState2D.height = canvasHeight;
        (_a = window['Laya3D']) === null || _a === void 0 ? void 0 : _a._changeWebGLSize(canvasWidth, canvasHeight);
        LayaGL.renderEngine.resizeOffScreen(canvasWidth, canvasHeight);
        if (TextRenderConfig.scaleFontWithCtx) {
            let fontScale = Math.max(Math.max(1, Math.min(TextRenderConfig.maxFontScale, ILaya.stage.scaleX)), Math.max(1, Math.min(TextRenderConfig.maxFontScale, ILaya.stage.scaleY)));
            if (TextRenderConfig.fontScale !== fontScale) {
                TextRenderConfig.fontScale = fontScale;
                const repaintTexts = (p) => {
                    for (let child of p._children) {
                        if ((child._renderType & SpriteConst.TEXT) !== 0)
                            child.repaint();
                    }
                };
                repaintTexts(this);
            }
        }
        this.visible = true;
        this.repaint();
        this.event(Event.RESIZE);
    }
    get scaleMode() {
        return this._scaleMode;
    }
    set scaleMode(value) {
        this._scaleMode = value;
        this.updateCanvasSize(true);
    }
    get alignH() {
        this.needUpdateCanvasSize();
        return this._alignH;
    }
    set alignH(value) {
        this._alignH = value;
        this.updateCanvasSize(true);
    }
    get alignV() {
        this.needUpdateCanvasSize();
        return this._alignV;
    }
    set alignV(value) {
        this._alignV = value;
        this.updateCanvasSize(true);
    }
    get bgColor() {
        return this._bgColor;
    }
    set bgColor(value) {
        this._bgColor = value;
        if (value) {
            let colorArr = ColorUtils.create(value).arrColor;
            this._wgColor.setValue(colorArr[0], colorArr[1], colorArr[2], colorArr[3]);
        }
        else
            this._wgColor.setValue(0, 0, 0, 0);
        if (Browser.isDomSupported)
            Browser.mainCanvas.source.style.background = value !== null && value !== void 0 ? value : "none";
    }
    get mouseX() {
        return Math.round(InputManager.mouseX / this.clientScaleX);
    }
    get mouseY() {
        return Math.round(InputManager.mouseY / this.clientScaleY);
    }
    getMousePoint() {
        return Point.TEMP.setTo(this.mouseX, this.mouseY);
    }
    get clientScaleX() {
        this.needUpdateCanvasSize();
        return this._scaleX;
    }
    get clientScaleY() {
        this.needUpdateCanvasSize();
        return this._scaleY;
    }
    get screenMode() {
        return this._screenMode;
    }
    set screenMode(value) {
        this._screenMode = value;
    }
    getFrameTm() {
        return this._frameStartTime;
    }
    getTimeFromFrameStart() {
        return Browser.now() - this._frameStartTime;
    }
    get visible() {
        return super.visible;
    }
    set visible(value) {
        super.visible = value;
        if (Browser.isDomSupported)
            Browser.mainCanvas.source.style.visibility = value ? "visible" : "hidden";
    }
    render() {
        if (this._frameRate === Stage.FRAME_SLEEP) {
            var now = Browser.now();
            if (now - this._frameStartTime < 1000)
                return;
            this._frameStartTime = now;
        }
        else {
            if (!this._visible) {
                this._renderCount++;
                if (this._renderCount % 5 === 0) {
                    Timer.callLaters._update();
                    Stat.loopCount++;
                    RenderInfo.loopCount = Stat.loopCount;
                    this._runComponents();
                    this._updateTimers();
                }
                return;
            }
            this._frameStartTime = Browser.now();
            RenderInfo.loopStTm = this._frameStartTime;
        }
        this._renderCount++;
        let frameMode = this._frameRate === Stage.FRAME_MOUSE ? (((this._frameStartTime - InputManager.lastMouseTime) < 2000) ? Stage.FRAME_FAST : Stage.FRAME_SLOW) : this._frameRate;
        let isFastMode = (frameMode !== Stage.FRAME_SLOW);
        let isDoubleLoop = (this._renderCount % 2 === 0);
        if (!isFastMode && !isDoubleLoop)
            return;
        Timer.callLaters._update();
        Stat.loopCount++;
        RenderInfo.loopCount = Stat.loopCount;
        LayaGL.renderEngine.startFrame();
        if (this.renderingEnabled) {
            this._runComponents();
            for (let i = 0, n = this._scene2Ds.length; i < n; i++) {
                this._scene2Ds[i]._update();
            }
            for (let i = 0, n = this._scene3Ds.length; i < n; i++) {
                this._scene3Ds[i]._update();
            }
            this._componentDriver.callPreRender();
            Render2DProcessor.rendercontext2D.setRenderTarget(null, true, this._wgColor);
            let t = Browser.now();
            for (let i = 0, n = this._scene3Ds.length; i < n; i++)
                this._scene3Ds[i].renderSubmit();
            LayaGL.statAgent.recordTimeData(StatElement.T_AllRender3D, Browser.now() - t);
            t = Browser.now();
            this._render2d();
            LayaGL.statAgent.recordTimeData(StatElement.T_AllRender2D, Browser.now() - t);
            this._componentDriver.callPostRender();
        }
        else
            this._runComponents();
        this._updateTimers();
        LayaGL.renderEngine.endFrame();
    }
    _render2d() {
        for (let i = 0, n = this._scene2Ds.length; i < n; i++) {
            this._scene2Ds[i].render(0, 0);
        }
        for (let sprite of this._subpassUpdateList) {
            if (sprite._destroyed || !sprite._subpassUpdateFlag)
                continue;
            sprite.updateSubRenderPassState();
            if (!sprite._oriRenderPass) {
                sprite._subpassUpdateFlag = 0;
                continue;
            }
            let result = sprite.updateRenderTexture();
            let destrt = sprite._drawOriRT;
            if (!destrt) {
                sprite.setSubRenderPassState(false);
                continue;
            }
            if (sprite.mask) {
                sprite._oriRenderPass.mask = sprite.mask._subStruct;
            }
            if (result) {
                sprite._oriRenderPass.renderTexture = destrt;
            }
            let process = sprite._oriRenderPass.postProcess;
            if (process) {
                if (result ||
                    (sprite._subpassUpdateFlag & SubPassFlag.PostProcess)) {
                    process.setResource(destrt);
                    process.clearCMD();
                    process._render();
                }
                if (process.enabled) {
                    destrt = process._context.destination;
                }
            }
            sprite._subStructRender.updateQuat(sprite._drawOriRT, destrt);
            sprite._subStructRender._updateVertexSize();
            sprite._subpassUpdateFlag = 0;
        }
        this._updateMatrixList(this._tranMatrixUpdateList, Stat.loopCount);
        for (let sprite of this._graphicUpdateList) {
            if (sprite._graphics) {
                sprite._graphics._render(Render2DProcessor.runner);
            }
        }
        this.passManager.apply(Render2DProcessor.rendercontext2D);
        this._graphicUpdateList.clear();
        this._subpassUpdateList.clear();
        this._tranMatrixUpdateList.clear();
        Stat.render();
        Stat.render2DCount++;
    }
    _updateMatrixList(changeMatrixList, frame) {
        for (let sprite of changeMatrixList) {
            let trans = sprite.globalTrans;
            if (sprite._destroyed || !trans)
                continue;
            let matrix = trans.getMatrix();
            sprite._struct.renderMatrix = matrix;
            if (sprite._subStruct)
                sprite._subStruct.renderMatrix = matrix;
        }
    }
    _runComponents() {
        this._componentDriver.callStart();
        this._componentDriver.callUpdate();
        this._componentDriver.callLateUpdate();
        this._componentDriver.callDestroy();
    }
    _updateTimers() {
        ILaya.systemTimer._update();
        ILaya.physicsTimer._update();
        ILaya.timer._update();
        Tweener._runAll();
    }
    set fullScreenEnabled(value) {
        let canvas = Browser.mainCanvas.source;
        if (value) {
            canvas.addEventListener('mousedown', requestFullscreen);
            canvas.addEventListener('touchstart', requestFullscreen);
            PAL.browser.on("fullscreenchange", this, this.fullScreenChanged);
        }
        else {
            canvas.removeEventListener('mousedown', requestFullscreen);
            canvas.removeEventListener('touchstart', requestFullscreen);
            PAL.browser.off("fullscreenchange", this, this.fullScreenChanged);
        }
    }
    fullScreenChanged() {
        this.event(Event.FULL_SCREEN_CHANGE);
    }
    exitFullscreen() {
        PAL.browser.exitFullscreen();
    }
    get frameRate() {
        return this._frameRate;
    }
    set frameRate(value) {
        this._frameRate = value;
    }
}
Stage.SCALE_NOSCALE = "noscale";
Stage.SCALE_SHOWALL = "showall";
Stage.SCALE_NOBORDER = "noborder";
Stage.SCALE_FULL = "full";
Stage.SCALE_FIXED_WIDTH = "fixedwidth";
Stage.SCALE_FIXED_HEIGHT = "fixedheight";
Stage.SCALE_FIXED_AUTO = "fixedauto";
Stage.ALIGN_LEFT = "left";
Stage.ALIGN_RIGHT = "right";
Stage.ALIGN_CENTER = "center";
Stage.ALIGN_TOP = "top";
Stage.ALIGN_MIDDLE = "middle";
Stage.ALIGN_BOTTOM = "bottom";
Stage.SCREEN_NONE = "none";
Stage.SCREEN_HORIZONTAL = "horizontal";
Stage.SCREEN_VERTICAL = "vertical";
Stage.FRAME_FAST = "fast";
Stage.FRAME_SLOW = "slow";
Stage.FRAME_MOUSE = "mouse";
Stage.FRAME_SLEEP = "sleep";
function requestFullscreen() {
    PAL.browser.requestFullscreen();
    let canvas = Browser.mainCanvas.source;
    canvas.removeEventListener('mousedown', requestFullscreen);
    canvas.removeEventListener('touchstart', requestFullscreen);
}
function formatData(value) {
    if (Math.abs(value) < 0.000001)
        return 0;
    if (Math.abs(1 - value) < 0.001)
        return value > 0 ? 1 : -1;
    return value;
}
